import { Fragment, useContext, useEffect, useState } from "react";
import Link from "components/shared/Link";
import Separator from "components/shared/Separator";
import Icon from "components/shared/Icon";
import { Context } from "Context";
import Button from "components/shared/Button";
import useCheckMediaQuery from "hooks/useCheckMediaQuery";
import appConfig from "config.json";

import {
	getLangFromLangSelectorUrl,
	getLanguageCookies,
	processLanguageSelectorUrl,
	processLocale,
	setLanguageCookies,
	validateGdprDomainByBrand,
} from "utils/language";
import LanguageSelectorModalWithRegionsInformation from "./LanguageSelectorModalWithRegionsInformation";
import { mapString, objectIsEmpty } from "utils";

const { mobileMediaQuery } = appConfig;
const mainClass = "syndicated-language-selector";
const propRegionsInformation = "regionsInformation";
const containLinks = ["primary", "secondary", propRegionsInformation];
const toOneDimension = (arr) => [].concat(...arr);
const LanguageSelector = () => {
	const {
		headerData: { languageSelector },
		config: { allowedLocales, brand, locale },
		setModal,
		cartHasItems,
	} = useContext(Context);

	const isMobileView = useCheckMediaQuery(mobileMediaQuery);

	const [selection, setSelection] = useState("");

	let containerClasses = `${mainClass}__container`;
	let modalClassName = "";
	const accessibilityModalTitle =
		languageSelector?.accessibilityModalTitle || "Language Selector";
	const accessibilityLinkTitle =
		languageSelector?.accessibilityLinkTitle ||
		"{language}. {accessibilityModalTitle}, currently set to {language}";
	const accessibilitySelectionTitle = selection?.title
		?.replace("(", "- ")
		?.replace(")", "");

	if (languageSelector?.regionsInformation) {
		containerClasses = `${containerClasses} ${containerClasses}--with-regions`;
		modalClassName = "syndicated-modal--with-regions";
	}

	const handleLanguageClick = (e, url) => {
		if (url.match(/^http/) || !languageSelector?.cookiesToSet) return;
		let locale, preferredLanguage, localeCurrency;
		e.preventDefault();
		preferredLanguage = url.replaceAll("/", "");
		let [language, region] = preferredLanguage.split("-");
		if (region) {
			region = region.toUpperCase();
		}
		locale = `${language}${region ? `_${region}` : ""}`;
		if (languageSelector?.localeToCurrency) {
			localeCurrency = languageSelector.localeToCurrency?.[locale];
		}
		setLanguageCookies(
			getLanguageCookies(
				languageSelector.cookiesToSet,
				locale,
				localeCurrency,
				preferredLanguage
			)
		);
		window?.location.reload();
	};

	const renderLanguages = (
		languages,
		focusElem = null,
		focusElemScrollY = null
	) => {
		let localesToDisplay = null;
		if (allowedLocales) {
			localesToDisplay = allowedLocales.split(",");
		}
		return languages ? (
			<div className={`${mainClass}__container__content`}>
				{languages?.map((link, key) => {
					if (localesToDisplay) {
						let currentLocale = null;
						if (link?.href[0] === "/") {
							currentLocale = link.href.split("/")[1]?.toLowerCase();
						}
						if (currentLocale && !allowedLocales.includes(currentLocale)) {
							return null;
						}
					}

					return (
						<Link
							{...link}
							href={processLanguageSelectorUrl(link?.href, brand)}
							lang={getLangFromLangSelectorUrl(link?.href)}
							handleClick={(event) => handleLanguageClick(event, link?.href)}
							bypassBasePath={true}
							bypassHandleClick={
								languageSelector?.cookiesToSet &&
								link?.target?.includes("interstitial")
									? true
									: false
							}
							focusElem={focusElem}
							focusElemScrollY={focusElemScrollY}
							key={key}
						/>
					);
				})}
			</div>
		) : null;
	};

	const renderFullCartMessage = () => {
		return cartHasItems && languageSelector?.fullCartMessage ? (
			<div className={`${mainClass}__container__message`}>
				<p>{languageSelector.fullCartMessage}</p>
			</div>
		) : null;
	};

	const handleCancel = (focusElem) => {
		if (focusElem) {
			focusElem.focus();
		}
		setModal(false);
	};

	const modalContent = (focusElem, focusElemScrollY) => {
		return languageSelector?.regionsInformation ? (
			<LanguageSelectorModalWithRegionsInformation
				containerClasses={containerClasses}
				renderLanguages={renderLanguages}
				renderFullCartMessage={renderFullCartMessage}
			/>
		) : (
			<Fragment>
				{renderFullCartMessage()}
				<div className={containerClasses}>
					{renderLanguages(
						languageSelector?.primary,
						focusElem,
						focusElemScrollY
					)}
					{languageSelector?.primary && languageSelector?.secondary && (
						<Separator vertical />
					)}
					{renderLanguages(
						languageSelector?.secondary,
						focusElem,
						focusElemScrollY
					)}
				</div>
				<div className={`${mainClass}__footer`}>
					<Separator section="language" />
					<Button
						accessibleText={languageSelector?.accessibilityText}
						className={`${mainClass}__close`}
						onClick={() => handleCancel(focusElem)}
						secondary={!isMobileView}
						title={languageSelector?.title}
					/>
				</div>
			</Fragment>
		);
	};

	useEffect(() => {
		let selectedLocale = {};
		if (locale && languageSelector) {
			if (languageSelector.simplifiedSelector) {
				if (languageSelector.primary?.length) {
					selectedLocale = languageSelector.primary[0];
				}
			} else {
				const newLocale = processLocale(locale);
				const gdprDomain = validateGdprDomainByBrand(brand, newLocale);
				let locales = [];
				for (let key of containLinks) {
					if (languageSelector[key] && key === propRegionsInformation) {
						locales = toOneDimension(
							languageSelector[key].map((item) => item.links)
						);
					} else {
						locales = locales.concat(languageSelector[key] || []);
					}
				}
				if (gdprDomain) {
					selectedLocale = locales.find(({ href }) =>
						href?.includes(gdprDomain)
					);
				} else {
					selectedLocale = locales.find(({ href }) => {
						return newLocale === processLocale(href);
					});
				}
			}
		}
		setSelection(selectedLocale || {});
	}, [languageSelector, locale, brand]);

	if (
		!languageSelector ||
		(languageSelector?.descriptions &&
			objectIsEmpty(languageSelector.descriptions))
	) {
		return null;
	}

	const localeReload = (event) => {
		if (appConfig.brands[brand]?.languageSelectorReload) {
			event.preventDefault();
			window?.location.assign(event.currentTarget.getAttribute("href"));
		}
	};

	return (
		<div className={mainClass}>
			{languageSelector?.simplifiedSelector ? (
				<Link
					className={`${mainClass}__open`}
					title={selection?.title}
					href={processLanguageSelectorUrl(selection?.href, brand)}
					handleClick={(event) => localeReload(event)}
					bypassBasePath
				>
					{isMobileView && (
						<Icon
							icon={languageSelector?.icon || "global-languages"}
							className={`syndicated-icon--${brand}`}
						/>
					)}
					<span>{selection?.title}</span>
				</Link>
			) : (
				<button
					aria-label={
						accessibilitySelectionTitle &&
						mapString(
							accessibilityLinkTitle,
							accessibilitySelectionTitle,
							accessibilityModalTitle,
							accessibilitySelectionTitle
						)
					}
					className={`${mainClass}__open`}
					onClick={(e) => {
						const focusElem = e.currentTarget;
						const focusElemScrollY = window?.scrollY;
						setModal({
							accessibilityTitle: accessibilityModalTitle,
							content: modalContent(focusElem, focusElemScrollY),
							className: modalClassName,
						});
					}}
					tabIndex={0}
				>
					<Icon
						icon={languageSelector?.icon || "global-languages"}
						className={`syndicated-icon--${brand}`}
						loading="eager"
					/>
					<span>{selection?.title}</span>
				</button>
			)}
		</div>
	);
};

export default LanguageSelector;
